<template>
  <div class="mx-3 mx-md-0">
    <section-title has-line content="Información de la Alternativa" />
    <validation-observer ref="formRef">
      <div class="row mb-4">
        <div class="col col-sm col-12">
          <validation-provider
            v-slot="{ errors }"
            rules="required"
            mode="eager"
          >
            <custom-drop-down
              v-model="formData.alternativeCategory"
              label="Categoría de la Alternativa:"
              placeholder="Seleccione"
              fieldkey="id"
              fieldname="name"
              :options="catalog.categories"
              :error="errors[0]"
              :disabled="isReadOnly"
              custom-error-msg="La categoría de la alternativa es requerida."
              @input="handleSelectedCategory"
            />
          </validation-provider>
        </div>
        <div class="col col-sm col-12 mt-3 mt-md-0">
          <validation-provider
            v-slot="{ errors }"
            rules="required"
            mode="eager"
          >
            <custom-drop-down
              v-model="formData.alternativeName"
              label="Nombre de la Alternativa:"
              placeholder="Seleccione"
              fieldkey="id"
              fieldname="name"
              :options="catalog.names"
              :disabled="isReadOnly"
              :error="errors[0]"
              custom-error-msg="El nombre de la alternativa es requerida."
            />
          </validation-provider>
        </div>
        <div
          v-if="showStudentCountInGeneralForm"
          class="col col-sm col-12 mt-3 mt-md-0"
        >
          <validation-provider
            v-slot="{ errors }"
            rules="required"
            mode="eager"
          >
            <custom-input
              v-model.number="formData.studentCount"
              label="Cantidad Estudiantes:"
              class="ml-md-2 mt-1 mt-md-0"
              type="number"
              :error="errors[0]"
              custom-error-msg="La cantidad de estudiantes es requerida."
            />
          </validation-provider>
        </div>
        <div
          v-else-if="showStudentCountReadOnly"
          class="col col-sm col-12 mt-3 mt-md-0"
        >
          <div class="color-gray-6 font-14">Cantidad Estudiantes:</div>
          <div class="color-black font-16 font-weight-normal label-value">
            {{ formData.studentCount }}
          </div>
        </div>
        <div
          v-else-if="showStudentCountEditable"
          class="col col-sm col-12 mt-3 mt-md-0 d-flex align-items-center"
        >
          <validation-observer
            ref="studentCountFormRef"
            class="flex-grow-1 student-count-field-wrapper"
          >
            <validation-provider
              v-slot="{ errors }"
              rules="required"
              mode="eager"
            >
              <custom-input
                v-model.number="studentCountDraft"
                label="Cantidad Estudiantes:"
                class="ml-md-2 mt-1 mt-md-0"
                type="number"
                :error="errors[0]"
                error-class="student-count-error"
                custom-error-msg="La cantidad de estudiantes es requerida."
              />
            </validation-provider>
          </validation-observer>
          <div class="mt-4 pt-2">
            <ActionButton
              class="ml-2"
              text="Guardar"
              i="fas fa-save"
              variant="ab-btn ab-btn-success"
              @click="handleSaveStudentCount"
            />
          </div>
        </div>
      </div>
    </validation-observer>
  </div>
</template>
<script>
// libs
import SectionTitle from 'SectionTitle';
import CustomDropDown from 'custom-drop-down';
import CustomInput from 'CustomInput';
import ActionButton from 'ActionButton';

// mixins
import mixAlert from '@/mixin/alert.mixin';

// services
import * as api from '@/views/pei/location-alternatives/_api';

export default {
  name: 'AlternativeInformation',
  mixins: [mixAlert],
  components: {
    SectionTitle,
    CustomDropDown,
    CustomInput,
    ActionButton,
  },
  props: {
    academicYearId: {
      type: Number,
      required: true,
    },
    actionPlanId: {
      type: Number,
      default: null,
    },
    actionPlanData: {
      type: Object,
      default: () => ({}),
    },
    isReadOnly: {
      type: Boolean,
      default: () => false,
    },
    isNewActionPlan: {
      type: Boolean,
      default: () => false,
    },
  },
  data() {
    return {
      formData: {
        alternativeCategory: this.actionPlanData.category,
        alternativeName: this.actionPlanData.name,
        studentCount: this.actionPlanData.studentCount,
      },
      studentCountDraft: this.actionPlanData.studentCount,
      catalog: {
        categories: [],
        names: [],
      },
    };
  },
  async mounted() {
    this.catalog.categories = await api.getCategories();
  },
  computed: {
    hasStudentCount() {
      return (
        this.formData.studentCount !== null &&
        this.formData.studentCount !== undefined
      );
    },
    showStudentCountInGeneralForm() {
      return this.isNewActionPlan || !this.isReadOnly;
    },
    showStudentCountReadOnly() {
      return !this.showStudentCountInGeneralForm && this.hasStudentCount;
    },
    showStudentCountEditable() {
      return !this.showStudentCountInGeneralForm && !this.hasStudentCount;
    },
  },
  methods: {
    async handleSelectedCategory(value) {
      this.formData.alternativeName = null;
      this.catalog.names = [];
      if (value?.id) {
        this.catalog.names = await api.getNames({
          academicYearId: this.academicYearId,
          categoryId: value.id,
        });
      }
    },
    async validateForm() {
      return await this.$refs.formRef.validate();
    },
    async resetForm() {
      this.$refs.formRef.reset();
    },
    getModel() {
      return { ...this.formData };
    },
    async handleSaveStudentCount() {
      const isValid = await this.$refs.studentCountFormRef.validate();
      if (!isValid) return;

      try {
        await api.updateActionPlanStudentCount(
          this.actionPlanId,
          this.studentCountDraft
        );
        this.formData.studentCount = this.studentCountDraft;
        this.ShowToast(
          '',
          'La cantidad de estudiantes se ha guardado correctamente.',
          'success'
        );
        this.$emit('refresh');
      } catch (error) {
        this.ShowToast('Error', error.response?.data?.message, 'error');
      }
    },
  },
  watch: {
    actionPlanData: {
      async handler(newVal) {
        this.formData.alternativeCategory = newVal.category;
        await this.handleSelectedCategory(newVal.category);
        this.formData.alternativeName = newVal.name;
        this.formData.studentCount = newVal.studentCount;
        this.studentCountDraft = newVal.studentCount;
        this.resetForm();
      },
      deep: true,
    },
  },
};
</script>
<style scoped>
.student-count-field-wrapper {
  position: relative;
}

.student-count-field-wrapper >>> .student-count-error {
  position: absolute;
}
</style>
